import { UploadedPhotoType } from "@features/loras/use-upload-photos";
import { CheckIcon } from "@heroicons/react/24/solid";
import { Text } from "@radix-ui/themes";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { twMerge } from "tailwind-merge";

interface ProgressBarProps {
  photos: UploadedPhotoType[];
  subtype: "avatar" | "animal" | "object" | "car" | "style" | "none";
  minPhotoCount: number;
  onAvailableModelChange?: (model: "agile" | "expression" | "none") => void;
  onGuidesChange?: (guides: string[]) => void;
  upgrade?: boolean;
}

const freeModelMinPhotos = 1;

export const ProgressBar = ({
  photos,
  subtype,
  minPhotoCount,
  onAvailableModelChange,
  onGuidesChange,
  upgrade,
}: ProgressBarProps) => {
  const { t } = useTranslation();

  /** Returns a value between 0 and 1, for faces, 0.75 is enough for a "Agile" training */
  const computeScore = (
    photos: UploadedPhotoType[],
  ): {
    score: number;
    model: "agile" | "expression" | "none";
    guides: string[];
  } => {
    if (photos.length === 0)
      return { score: 0, model: "none", guides: ["first_photo"] };
    let score = 0;

    if (subtype === "avatar") {
      const hasHighQualityPicture = photos.some(
        (p) =>
          (p.details?.pose === "front" || subtype !== "avatar") &&
          p.score >= 60,
      );

      // The first part of the score (0->0.4) is for the agile model
      const hasInitialPictures =
        Math.min(freeModelMinPhotos, photos.length) / freeModelMinPhotos;
      score = hasInitialPictures * 0.3 + (hasHighQualityPicture ? 0.1 : 0);

      if (score < 0.4) {
        return {
          score: score,
          model: "none",
          guides: hasHighQualityPicture
            ? ["enough_photos_free_model"]
            : ["avatar_quality"],
        };
      }

      // 0.4 -> 0.8 is the number of photos divided by the required number of photos
      score += Math.min(1, photos.length / minPhotoCount) * 0.4;
      let guides = [];
      if (photos.length < minPhotoCount) {
        guides.push(
          photos.length < 10 ? "enough_photos_first" : "enough_photos",
        );
      }

      // 0.1 is for having at least 2 high-quality pictures
      if (photos.filter((p) => p.score >= 60)?.length >= 2) {
        score += 0.1;
      } else {
        guides.push("quality");
      }

      // Last 0.1 is for having different poses
      const poses = new Set(
        photos
          .filter((p) => p.details?.pose)
          .map((p) => p.details?.pose as string),
      );
      if (poses.size >= 2) {
        score += 0.1;
      } else {
        guides.push("poses");
      }

      // TEMPORARILY DISABLED: Intermediate "agile" model for avatars
      // Previously: model: score >= 1 ? "expression" : upgrade ? "none" : "agile"
      // Now we only allow "expression" (full training) or "none" for avatars
      return {
        score: score,
        model: score >= 1 ? "expression" : "none",
        guides,
      };
    } else {
      score = Math.min(1, photos.length / minPhotoCount) * 0.8;
      let guides = [];
      if (photos.length < minPhotoCount) {
        guides.push(
          photos.length < 10 ? "enough_photos_first" : "enough_photos",
        );
      }

      if (photos.filter((p) => p.score >= 40)?.length >= 2) {
        score += 0.2;
      } else {
        guides.push("quality");
      }

      return {
        score,
        model:
          score >= 1 ? (subtype === "style" ? "expression" : "agile") : "none",
        guides,
      };
    }
  };

  const { score, model, guides } = computeScore(photos);

  useEffect(() => {
    if (onAvailableModelChange) {
      onAvailableModelChange(model);
    }
  }, [model, onAvailableModelChange]);

  useEffect(() => {
    if (onGuidesChange) {
      onGuidesChange(guides);
    }
  }, [guides, onGuidesChange]);

  // Get progress bar color based on score
  const getProgressColor = (score: number) => {
    if (score >= 1.0) return "bg-green-500 shadow-green-200"; // Perfect - Green
    if (score >= 0.25) return "bg-orange-400 shadow-orange-200"; // Good - Yellow
    return "bg-red-400 shadow-red-200"; // Needs work - Red
  };

  // Get guide message with emoji
  const getGuideMessage = (guide: string) => {
    const getTranslationKey = (guideKey: string) =>
      `loras.upload.progress.guides.${guideKey}`;

    switch (guide) {
      case "first_photo":
        return t(getTranslationKey("first_photo"));
      case "enough_photos_first":
        return t(getTranslationKey("enough_photos_first"));
      case "enough_photos":
        return t(getTranslationKey("enough_photos"), {
          current: photos.length,
          needed: minPhotoCount,
        });
      case "enough_photos_free_model":
        return t(getTranslationKey("enough_photos_free_model"), {
          current: photos.length,
          needed: freeModelMinPhotos,
        });
      case "poses":
        return t(getTranslationKey("poses"));
      case "avatar_quality":
        return t(getTranslationKey("avatar_quality"));
      case "quality":
        return t(getTranslationKey("quality"));
      case "agile_to_expression":
        return t(getTranslationKey("agile_to_expression"));
      default:
        return t(getTranslationKey("default"));
    }
  };

  const isAgileReached = model === "agile" || model === "expression";
  const isExpressionReached = model === "expression";

  return (
    <div className="space-y-3">
      {/* Progress Bar */}
      <div className="rounded-b-md w-full bg-slate-50 h-7 border overflow-hidden relative dark:bg-slate-900 dark:border-slate-700">
        {/* Animated progress fill */}
        <div
          className={twMerge(
            "z-0 h-7 absolute top-0 left-0 transition-all duration-500 ease-out overflow-hidden",
            getProgressColor(score),
          )}
          style={{
            width: "100%",
            marginLeft: `${Math.max(0, Math.min(100, score * 100)) - 100}%`,
          }}
        >
          <div
            className="absolute inset-0 opacity-40"
            style={{
              background:
                "linear-gradient(90deg, transparent, rgba(255,255,255,0.6), transparent)",
              animation: "shimmer 2.5s linear infinite",
              width: "30%",
            }}
          />
        </div>
        {/* TEMPORARILY DISABLED: Two-checkpoint progress for avatars
            Re-enable by changing condition to: subtype === "avatar" && !upgrade */}
        {false && subtype === "avatar" && !upgrade ? (
          <>
            <div
              className="absolute z-10 h-7 flex items-center justify-center text-xs font-medium"
              style={{ right: "60%" }}
            >
              {isAgileReached ? (
                <div className="font-bold border-r border-white pr-2 h-7 flex items-center pb-0.5 text-white">
                  <CheckIcon className="h-4 w-4" />
                  <span>{t("loras.upload.progress.free_model")}</span>
                </div>
              ) : (
                <div className="text-slate-500 dark:text-slate-400 border-r pr-2 h-7 flex items-center pb-0.5">
                  {t("loras.upload.progress.free_model")}
                </div>
              )}
            </div>

            <div
              className={twMerge(
                "absolute z-10 h-7 flex items-center justify-center text-xs font-medium",
                isAgileReached ? "" : "opacity-50",
              )}
              style={{ right: "0%" }}
            >
              {isExpressionReached ? (
                <div className="font-bold pr-2 h-7 flex items-center pb-0.5 text-white">
                  <CheckIcon className="h-4 w-4" />
                  <span>{t("loras.upload.progress.best_model")}</span>
                </div>
              ) : (
                <div className="text-slate-500 dark:text-slate-400 pr-2 h-7 flex items-center pb-0.5">
                  {t("loras.upload.progress.best_model")}
                </div>
              )}
            </div>
          </>
        ) : (
          <div
            className="absolute z-10 h-7 flex items-center justify-center text-xs font-medium"
            style={{ right: "0%" }}
          >
            {score < 1 ? (
              <div className="font-bold text-slate-500 dark:text-slate-400 pr-2 h-7 flex items-center pb-0.5">
                {t("loras.upload.progress.almost_there")}
              </div>
            ) : (
              <div className="font-bold pr-2 h-7 flex items-center pb-0.5 text-white">
                <CheckIcon className="h-4 w-4" />
                <span>{t("loras.upload.progress.perfect")}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Guide Message */}
      {guides.length > 0 && (
        <div className="flex items-center justify-center space-x-2 text-center w-full">
          <Text size="2" color="gray" className="leading-relaxed">
            {getGuideMessage(guides[0])}
          </Text>
        </div>
      )}
    </div>
  );
};
